import { useCallback, useEffect, useState } from 'react';
import { useFi } from '../../contexts/fi-context';
import { FI_ORANGE_DROP_SHADOW_CLASS } from './fi-styles';
import { cn } from '../../utils/cn';

const DELAY_MS = 4000;
const INTRO_TEXT = `Hello, I'm Fi — an AI Agent. I can't have personal conversations, but I can help you find the right kind of support here.`;
const TYPE_SPEED_MS = 25;
const EXIT_DURATION_MS = 300;

/**
 * Use a delayed flag to determine if the intro message should be shown.
 * @param delayMs - The delay in milliseconds.
 * @param enabled - Whether the delayed flag is enabled (i.e. the intro is not dismissed and the panel is not open).
 * @returns The ready state (true if the delay has passed and the intro is not dismissed and the panel is not open).
 */
function useDelayedFlag(delayMs: number, enabled: boolean) {
  const [ready, setReady] = useState(false);

  useEffect(() => {
    if (!enabled) return;
    const t = window.setTimeout(() => setReady(true), delayMs);
    return () => window.clearTimeout(t);
  }, [delayMs, enabled]);

  useEffect(() => {
    if (!enabled) setReady(false);
  }, [enabled]);

  return ready;
}

/**
 * Typewriter effect for the intro message.
 * @param text - The text to type.
 * @param enabled - Whether the typewriter effect is enabled.
 * @param speedMs - The speed of the typewriter effect.
 * @returns The number of characters typed.
 */
function useTypewriter(text: string, enabled: boolean, speedMs: number) {
  const [charCount, setCharCount] = useState(0);

  useEffect(() => {
    if (!enabled) {
      setCharCount(0);
      return;
    }
    if (charCount >= text.length) return;
    const t = window.setTimeout(() => setCharCount((c) => c + 1), speedMs);
    return () => window.clearTimeout(t);
  }, [enabled, charCount, speedMs, text.length]);

  return charCount;
}

export const FiIntroMessage = () => {
  const { introDismissed, dismissIntro, panelOpen, setIntroVisible } = useFi();
  const [entered, setEntered] = useState(false);
  const [exiting, setExiting] = useState(false);
  // Keep the intro mounted while exiting even if the panel opens, so the out animation can play.
  const ready = useDelayedFlag(DELAY_MS, !introDismissed && (!panelOpen || exiting));
  const charCount = useTypewriter(INTRO_TEXT, ready && !exiting, TYPE_SPEED_MS);
  const showIntroShadow = ready && !exiting; // Show intro shadow when intro is visible

  useEffect(() => {
    if (!ready || exiting) return;
    // Trigger a transition after initial render.
    const t = window.setTimeout(() => setEntered(true), 0);
    return () => window.clearTimeout(t);
  }, [ready, exiting]);

  const handleExit = useCallback(() => {
    if (exiting) return;
    setExiting(true);
    setEntered(false);
    window.setTimeout(() => dismissIntro(), EXIT_DURATION_MS);
  }, [exiting, dismissIntro]);

  // Update intro visibility state
  useEffect(() => {
    const visible = ready && !exiting && !introDismissed && !panelOpen;
    setIntroVisible(visible);
    return () => setIntroVisible(false);
  }, [introDismissed, panelOpen, ready, exiting, setIntroVisible]);

  // Auto-dismiss intro when panel is opened
  useEffect(() => {
    if (panelOpen && ready && !exiting) handleExit();
  }, [panelOpen, ready, exiting, handleExit]);

  if (!exiting && (introDismissed || !ready)) return null; // Don't render if intro is dismissed or not ready

  return (
    <div className="bottom-7.5 right-13 pointer-events-none fixed z-40">
      <div
        className={cn(
          'pointer-events-auto relative origin-right transform transition-[opacity,transform] will-change-transform',
          exiting ? 'duration-300 ease-in' : 'duration-1000 ease-out',
          entered ? 'scale-x-100 opacity-100' : 'scale-x-0 opacity-0',
        )}
      >
        {/* Dismiss button */}
        <button
          type="button"
          aria-label="Dismiss introduction"
          onClick={handleExit}
          className="absolute -left-1 -top-1 z-10 grid h-5 w-5 place-items-center rounded-full border border-black/10 bg-white text-gray-600 shadow-[0px_4px_20px_rgba(0,0,0,0.12)] transition-colors hover:bg-gray-50"
        >
          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" aria-hidden>
            <path d="M18 6L6 18M6 6l12 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
          </svg>
        </button>
        {/* Message Pill */}
        <div
          className={cn(
            'bg-concierge-pale rounded-br-r-15 rounded-tr-r-15 px-4.5 relative z-0 h-[72px] w-80 rounded-bl-[27px] rounded-tl-[25px] py-4 shadow-[0px_4px_20px_rgba(0,0,0,0.12)]',
            showIntroShadow && FI_ORANGE_DROP_SHADOW_CLASS,
          )}
        >
          <div className="relative h-full">
            <p
              className="font-open-sans text-muted-text-gray invisible ml-1.5 w-60 text-xs font-normal leading-[18px] tracking-[-0.266px]"
              aria-hidden
            >
              {INTRO_TEXT}
            </p>
            <p className="font-open-sans text-muted-text-gray absolute -top-2 left-0 ml-1.5 w-60 text-xs font-normal leading-[18px] tracking-[-0.266px]">
              {INTRO_TEXT.slice(0, charCount)}
              {charCount < INTRO_TEXT.length && (
                <span
                  className="animate-fi-cursor-blink ml-0.5 inline-block h-4 w-px bg-gray-500 align-middle"
                  aria-hidden
                />
              )}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};
